import {
  TENANT_FEATURE_DEPENDENCIES,
  type TenantFeature,
  type TenantFeatureFlags,
} from "../../shared/tenant";
import { TENANT_FEATURE_LABELS } from "./types";

/**
 * Soft dependency hints for tenant feature toggles.
 * Client only warns; backend validate/persist is source of truth and does not auto-cascade.
 */
export function getMissingFeatureDependencies(
  features: TenantFeatureFlags,
  key: TenantFeature
): TenantFeature[] {
  if (!features[key]) return [];
  const deps = TENANT_FEATURE_DEPENDENCIES[key] || [];
  return deps.filter((dep) => !features[dep]);
}

export function describeMissingFeatureDependencies(
  features: TenantFeatureFlags,
  key: TenantFeature
): string | null {
  const missing = getMissingFeatureDependencies(features, key);
  if (missing.length === 0) return null;
  const labels = missing.map((item) => TENANT_FEATURE_LABELS[item]).join("、");
  return `建议同时开启：${labels}（仅提示，后端不会自动级联）`;
}

export function collectFeatureDependencyWarnings(
  features: TenantFeatureFlags
): string[] {
  return (Object.keys(features) as TenantFeature[])
    .map((key) => describeMissingFeatureDependencies(features, key))
    .filter((item): item is string => Boolean(item));
}

/**
 * Keep feature flags valid while editing:
 * enabling a feature enables all prerequisites; disabling one disables all dependants.
 */
export function applyTenantFeatureToggle(
  current: TenantFeatureFlags,
  key: TenantFeature,
  checked: boolean
): TenantFeatureFlags {
  const next = { ...current, [key]: checked };

  if (checked) {
    const enableDependencies = (feature: TenantFeature) => {
      for (const dependency of TENANT_FEATURE_DEPENDENCIES[feature] || []) {
        next[dependency] = true;
        enableDependencies(dependency);
      }
    };
    enableDependencies(key);
    return next;
  }

  const disableDependants = (dependency: TenantFeature) => {
    for (const feature of Object.keys(next) as TenantFeature[]) {
      if (!next[feature]) continue;
      if (!(TENANT_FEATURE_DEPENDENCIES[feature] || []).includes(dependency)) continue;
      next[feature] = false;
      disableDependants(feature);
    }
  };
  disableDependants(key);
  return next;
}
